import React, { useState, useEffect } from "react";
import { FileText, CircleCheck, CheckCircle2, CreditCard, Edit, X, Check, AlertCircle } from "lucide-react";
import { RoleHelper } from "src/helpers";
import { POInvoiceStatus } from "src/types";
import { useAuthorisation } from "src/context";
import { SelectInputBox } from "src/components/ui/inputs";

interface EditInvoiceStatusProps {
  invoiceStatus?: POInvoiceStatus;
  purchaseOrderId?: string;
  onUpdateInvoiceStatus?: (status: POInvoiceStatus) => void;
  isUpdatingInvoiceStatus?: boolean;
  invoiceStatusUpdateError?: Error | null;
  variant?: "limited" | "full";
}

const getInvoiceStatusConfig = (status?: POInvoiceStatus) => {
  switch (status) {
    case "UPLOADED":
      return {
        label: "Uploaded",
        badgeClass: "border border-blue-300 bg-blue-50 text-blue-800",
        icon: FileText,
        iconClass: "text-blue-600"
      };
    case "VERIFIED":
      return {
        label: "Verified",
        badgeClass: "border border-purple-300 bg-purple-50 text-purple-800",
        icon: CircleCheck,
        iconClass: "text-purple-600"
      };
    case "APPROVED":
      return {
        label: "Approved",
        badgeClass: "border border-success-300 bg-success-50 text-success-800",
        icon: CheckCircle2,
        iconClass: "text-success-600"
      };
    case "PAID":
      return {
        label: "Paid",
        badgeClass: "border border-emerald-300 bg-emerald-50 text-emerald-800",
        icon: CreditCard,
        iconClass: "text-emerald-600"
      };
    default:
      return null;
  }
};

const EditInvoiceStatus: React.FC<EditInvoiceStatusProps> = ({
  invoiceStatus,
  purchaseOrderId,
  onUpdateInvoiceStatus,
  isUpdatingInvoiceStatus = false,
  invoiceStatusUpdateError,
  variant = "full"
}) => {
  const { userRole } = useAuthorisation();

  const isOpsUser = RoleHelper.isHealfOperationsTeam(userRole);
  const isFinanceRole = userRole === "HEALF_ACCOUNTANT";
  const isAdminRole = userRole === "ADMIN";

  const [isEditingStatus, setIsEditingStatus] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<POInvoiceStatus | "">(invoiceStatus || "");

  useEffect(() => {
    if (invoiceStatus) {
      setSelectedStatus(invoiceStatus);
    }
  }, [invoiceStatus]);

  const statusConfig = getInvoiceStatusConfig(invoiceStatus);

  // Determine available status options based on role
  const getStatusOptions = (): { value: POInvoiceStatus; label: string }[] => {
    if (isAdminRole) {
      // Admin has access to all statuses
      return [
        { value: "UPLOADED", label: "Uploaded" },
        { value: "VERIFIED", label: "Verified" },
        { value: "APPROVED", label: "Approved" },
        { value: "PAID", label: "Paid" }
      ];
    } else if (isFinanceRole) {
      // Finance can only set to VERIFIED, APPROVED, or PAID
      return [
        { value: "VERIFIED", label: "Verified" },
        { value: "APPROVED", label: "Approved" },
        { value: "PAID", label: "Paid" }
      ];
    } else if (isOpsUser) {
      // Ops can only set to UPLOADED or VERIFIED
      return [
        { value: "UPLOADED", label: "Uploaded" },
        { value: "VERIFIED", label: "Verified" }
      ];
    }
    return [];
  };

  const statusOptions = getStatusOptions();

  const canEditInvoiceStatus =
    purchaseOrderId &&
    invoiceStatus &&
    ((isOpsUser && !isAdminRole && statusOptions.some((option) => option.value === invoiceStatus)) ||
      (isFinanceRole && statusOptions.some((option) => option.value === invoiceStatus)) ||
      isAdminRole);

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value as POInvoiceStatus);
  };

  const handleSaveStatus = () => {
    if (selectedStatus && onUpdateInvoiceStatus && selectedStatus !== invoiceStatus) {
      onUpdateInvoiceStatus(selectedStatus);
      // Don't close edit mode here - let useEffect handle it on success
    }
    setIsEditingStatus(false);
  };

  const handleCancelEdit = () => {
    setSelectedStatus(invoiceStatus || "");
    setIsEditingStatus(false);
  };

  useEffect(() => {
    if (invoiceStatusUpdateError) {
      setIsEditingStatus(true);
    }
  }, [invoiceStatusUpdateError]);

  if (!invoiceStatus || !statusConfig) {
    return null;
  }

  // Limited view (read-only)
  if (variant === "limited") {
    return (
      <div className="rounded-xl border border-secondary-200 bg-white p-4 shadow-sm">
        <div className="flex items-center space-x-2 mb-2">
          <statusConfig.icon className={`w-4 h-4 ${statusConfig.iconClass}`} />
          <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Invoice Status</span>
        </div>
        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusConfig.badgeClass}`}
        >
          {statusConfig.label}
        </span>
      </div>
    );
  }

  // Full view (editable)
  return (
    <div className="rounded-2xl border border-secondary-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center space-x-2">
          <statusConfig.icon className={`w-5 h-5 ${statusConfig.iconClass}`} />
          <span className="text-xs font-semibold text-secondary-700 uppercase tracking-wide">Invoice Status</span>
          {isUpdatingInvoiceStatus && (
            <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin ml-2" />
          )}
        </div>
        {canEditInvoiceStatus && !isEditingStatus && !isUpdatingInvoiceStatus && (
          <button
            onClick={() => setIsEditingStatus(true)}
            className="p-1.5 rounded-lg hover:bg-secondary-100 transition-colors text-secondary-600 hover:text-primary-600"
            title="Edit invoice status"
          >
            <Edit className="w-4 h-4" />
          </button>
        )}
      </div>
      {isEditingStatus ? (
        <div className="space-y-3">
          <SelectInputBox
            id="invoice-status-select"
            value={selectedStatus}
            options={statusOptions}
            onChange={handleStatusChange}
            disabled={isUpdatingInvoiceStatus}
            size="sm"
            error={invoiceStatusUpdateError ? true : false}
          />
          {invoiceStatusUpdateError && (
            <div className="flex items-start gap-2 p-3 bg-error-50 border border-error-200 rounded-lg">
              <AlertCircle className="w-4 h-4 text-error-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-error-800">Failed to update status</p>
                <p className="text-xs text-error-700 mt-1">
                  {invoiceStatusUpdateError.message || "An error occurred. Please try again."}
                </p>
              </div>
            </div>
          )}
          <div className="flex items-center gap-2">
            <button
              onClick={handleSaveStatus}
              disabled={isUpdatingInvoiceStatus || selectedStatus === invoiceStatus}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-success-600 text-white rounded-lg hover:bg-success-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              {isUpdatingInvoiceStatus ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Save
                </>
              )}
            </button>
            <button
              onClick={handleCancelEdit}
              disabled={isUpdatingInvoiceStatus}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-secondary-200 text-secondary-700 rounded-lg hover:bg-secondary-300 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-colors"
            >
              <X className="w-4 h-4" />
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <span
            className={`inline-flex items-center rounded-full px-3 py-1.5 text-xs font-semibold uppercase tracking-wide ${statusConfig.badgeClass}`}
          >
            {statusConfig.label}
          </span>
        </>
      )}
    </div>
  );
};

export { EditInvoiceStatus };
